import { TZDate, tz as tzFn } from '@date-fns/tz';
import type { LatLong } from '@iheartjane/geolocation';
import { openingAndClosing } from '@iheartjane/menu';
import type {
  Nullable,
  Nullish,
  TimeRange,
  TimeZone,
  WorkingHour,
} from '@iheartjane/types';
import { timezone, timezoneToken } from '@iheartjane/utils';
import { add, format, isAfter, isBefore, isSameDay } from 'date-fns';

import type { BloomMenuStore } from '@jane/bloom/dataAccess/roots/storeApi/bloom/menu.server';
import { timeRangeForDay } from '@jane/bloom/utils/dates/timeRangeForDay';
import { getDistance } from '@jane/bloom/utils/geo/getDistance';
import type {
  StoreViewModel,
  WorkingHourDetails,
} from '@jane/bloom/viewModels/store';

type GetStoresWithDetailsArgs = {
  stores: BloomMenuStore[];
  userLocation: Partial<LatLong>;
};

export const formatTime = (date: Date, timezone: TimeZone) => {
  const hours = format(date, 'h', { in: tzFn(timezone) });
  const minutes = format(date, 'mm', { in: tzFn(timezone) });
  const period = format(date, 'aaaaa', { in: tzFn(timezone) });

  // Only include minutes if they're not '00'
  const timeString =
    minutes === '00' ? `${hours}${period}` : `${hours}:${minutes}${period}`;

  return timeString;
};

export const formatDay = (day: string) => {
  if (!day) return '';
  return day.charAt(0).toUpperCase() + day.slice(1, 3);
};

/**
 * Stores express round-the-clock hours as midnight to 11:59pm. Tomorrow must
 * also open at midnight, otherwise the store really does close tonight.
 */
export const isOpen24Hours = (
  today: Nullable<TimeRange>,
  tomorrow: Nullable<TimeRange>
) => {
  return (
    today?.from === '00:00' &&
    today?.to === '23:59' &&
    tomorrow?.from === '00:00'
  );
};

export const isOpenNow = (
  openingHours: Date,
  closingHours: Date,
  now: Date
) => {
  const opening = new Date(openingHours);
  const closing = new Date(closingHours);

  return isAfter(now, opening) && isBefore(now, closing);
};

export type NextOpening = {
  day: string;
  /** Whole days from `now`; 0 when the store opens later the same day. */
  daysFromNow: number;
  period: {
    from: string;
    to: string;
  };
};

/** Looks the day up by name, so the schedule's order and length don't matter. */
const hoursOn = (workingHours: WorkingHour[], date: Date) => {
  const dayName = format(date, 'eeee').toLowerCase();
  return workingHours.find((h) => h.day === dayName);
};

const toNextOpening = (
  workingHours: Nullish<WorkingHour>,
  daysFromNow: number
): NextOpening | null => {
  const from = workingHours?.period.from;
  const to = workingHours?.period.to;

  if (!workingHours || !from || !to) {
    return null;
  }

  return { day: workingHours.day, daysFromNow, period: { from, to } };
};

export const getNextOpeningHours = (
  workingHours: WorkingHour[],
  now: Date,
  willOpenLaterToday: boolean
): NextOpening | null => {
  if (!workingHours) {
    return null;
  }

  // Return current day's working hours if they will open later today
  if (willOpenLaterToday) {
    return toNextOpening(hoursOn(workingHours, now), 0);
  }

  // Check the next 6 days for an opening period
  for (let daysFromNow = 1; daysFromNow <= 6; daysFromNow++) {
    const nextOpening = toNextOpening(
      hoursOn(workingHours, add(now, { days: daysFromNow })),
      daysFromNow
    );

    if (nextOpening) {
      return nextOpening;
    }
  }

  // If no opening is found, return null
  return null;
};

const getNextOpeningTime = (
  workingHours: WorkingHour[],
  ianaTz: TimeZone,
  willOpenLaterToday: boolean
): WorkingHourDetails => {
  const zonedNow = new TZDate(new Date(), ianaTz);

  const nextOpening = getNextOpeningHours(
    workingHours,
    zonedNow,
    willOpenLaterToday
  );

  if (!nextOpening) {
    return {
      formattedHours: '',
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: false,
    };
  }

  const [nextOpeningTime, nextClosingTime] = openingAndClosing(
    nextOpening.period,
    ianaTz,
    add(zonedNow, { days: nextOpening.daysFromNow })
  );

  const nextOpeningTimeFormatted = formatTime(nextOpeningTime, ianaTz);
  /** Dated in the store's zone so a late-night opening can't land on the viewer's yesterday. */
  const nextOpeningDateFormatted = format(nextOpeningTime, 'MM/dd', {
    in: tzFn(ianaTz),
  });

  return {
    formattedHours: `Closed until ${formatDay(nextOpening.day)}, ${nextOpeningTimeFormatted} ${timezoneToken(
      ianaTz
    )}`,
    nextAvailableClosingTime: formatTime(nextClosingTime, ianaTz),
    /** Always dated — "closed until 9a" is ambiguous when the store reopens days from now. */
    nextAvailableOpeningTime: `${nextOpeningDateFormatted} ${nextOpeningTimeFormatted}`,
    openNow: false,
  };
};

type WorkingHourDetailsArgs = {
  todaysHours: Nullable<TimeRange>;
  tomorrowsHours: Nullable<TimeRange>;
  tz: TimeZone;
  workingHours: WorkingHour[];
};

const workingHourDetails = ({
  todaysHours,
  tomorrowsHours,
  tz,
  workingHours,
}: WorkingHourDetailsArgs): WorkingHourDetails => {
  if (isOpen24Hours(todaysHours, tomorrowsHours)) {
    return {
      formattedHours: 'Open 24 hours',
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: true,
    };
  }

  const ianaTz = timezone(tz);
  const zonedNow = new TZDate(new Date(), ianaTz);

  let opening: Date | undefined;
  let closing: Date | undefined;

  if (todaysHours && todaysHours?.from && todaysHours?.to) {
    [opening, closing] = openingAndClosing(
      todaysHours as { from: string; to: string },
      tz
    );
  }

  const willOpenLaterToday =
    opening && isAfter(opening, zonedNow) && isSameDay(opening, zonedNow);

  if (opening && closing && isOpenNow(opening, closing, zonedNow)) {
    return {
      formattedHours: `Open until ${formatTime(closing, ianaTz)} ${timezoneToken(ianaTz)}`,
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: true,
    };
  }

  return getNextOpeningTime(workingHours, ianaTz, !!willOpenLaterToday);
};

export const getStoresWithDetails = ({
  stores,
  userLocation,
}: GetStoresWithDetailsArgs): StoreViewModel[] => {
  return stores.map((store) => {
    /** Zoned so adding a day moves one calendar day at the store, even across DST. */
    const zonedNow = new TZDate(
      new Date(),
      timezone(store.time_zone_identifier)
    );
    const todaysHours = timeRangeForDay(
      store.working_hours,
      store.time_zone_identifier,
      zonedNow
    );
    const tomorrowsHours = timeRangeForDay(
      store.working_hours,
      store.time_zone_identifier,
      add(zonedNow, { days: 1 })
    );

    let distance;
    const d = getDistance(
      userLocation?.lat,
      userLocation?.long,
      store.latitude,
      store.longitude
    );
    if (isNaN(d)) {
      distance = '';
    } else {
      distance = `${d.toFixed(d > 20 ? 0 : 1)}mi`;
    }

    return {
      ...store,
      ...workingHourDetails({
        todaysHours,
        tomorrowsHours,
        tz: store.time_zone_identifier,
        workingHours: store.working_hours || [],
      }),
      distance,
    };
  });
};
